import { Icon, IconButton, ListSubheader, MenuItem } from "@material-ui/core";
import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { Trans } from "react-i18next";
import {
    ActionPermissionsObject,
    GET_DEFAULT_RESTRICTED_PERMISSIONS_ACCESS,
    IWorkspacePermissionAccess,
    IWorkspaceResourceFolder,
    PermissionActionType,
    WorkspaceResourceFolderType,
    WorkspaceResourceType,
    WorkspacePlanTier
} from "common/interfaces";
import WorkspaceController, {
    getUserPermissionsToResourceFolder,
    WorkspaceControllerState,
} from "js/controllers/WorkspaceController";
import getLogger from "js/core/logger";
import ux from "js/core/services/userExperiences";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { app } from "js/namespaces";
import UpgradeIcon from "js/Components/UpgradeIcon";
import { UpgradeTooltip } from "js/Components/UpgradeTooltip";
import {
    ShowConfirmationDialog,
    ShowDialog,
    ShowErrorDialog,
    ShowSnackBar,
    ShowUpgradeDialog,
    ShowWarningDialog
} from "js/react/components/Dialogs/BaseDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import CreateFolderDialog from "js/react/components/Dialogs/CreateFolderDialog";
import { Divider, IconMenu, UIPaneSideBarResizer } from "js/react/components/UiComponents";
import { themeColors } from "js/react/sharedStyles";
import { UpgradePlanDialogType } from "js/react/views/MarketingDialogs/UpgradePlanDialog";
import { getAddToFolderSnackbarProps } from "js/editor/PresentationLibrary/utils/getAddToFolderSnackbarProps";
import { isCompactLibrary } from "js/editor/PresentationLibrary/utils/isCompactLibrary";
import {
    FolderMenuItems,
    SubFolderMenuItems,
} from "js/editor/PresentationLibrary/components/PresentationLibraryFolderMenu";
import { t } from "js/i18n";
import PresentationLibraryPaneController, {
    PresentationLibraryFilter,
    PresentationLibraryFilterType,
    PresentationLibraryPresentation,
} from "js/controllers/PresentationLibraryPaneController";
import {
    getWorkspaceResourcesSidebarWidth,
    saveWorkspaceResourcesSidebarWidth,
} from "js/react/views/SharedResources/Components/WorkspaceResourcesSidebarHelper";

const logger = getLogger();

const MIN_SIDEBAR_WIDTH = 200;
const MAX_SIDEBAR_WIDTH = 400;
const LIBRARY_FOLDER_SORT_STORAGE_KEY = "bai-library-folder-sort";

enum LibraryFolderSortMode {
    NAME = "name",
    SHARED_FIRST = "shared_first",
    PRIVATE_FIRST = "private_first",
}

function clampSidebarWidth(width: number) {
    return Math.max(MIN_SIDEBAR_WIDTH, Math.min(MAX_SIDEBAR_WIDTH, width));
}

function getLibraryFolderSortMode(): LibraryFolderSortMode {
    const stored = localStorage.getItem(LIBRARY_FOLDER_SORT_STORAGE_KEY);
    if (
        stored === LibraryFolderSortMode.NAME ||
        stored === LibraryFolderSortMode.SHARED_FIRST ||
        stored === LibraryFolderSortMode.PRIVATE_FIRST
    ) {
        return stored;
    }
    return LibraryFolderSortMode.NAME;
}

function compareFoldersByName(a: IWorkspaceResourceFolder, b: IWorkspaceResourceFolder) {
    return a.name.localeCompare(b.name);
}

function sortLibraryFolders(folders: IWorkspaceResourceFolder[], mode: LibraryFolderSortMode) {
    return [...folders].sort((a, b) => {
        if (mode !== LibraryFolderSortMode.NAME) {
            const aShared = WorkspaceController.isFolderSharedWithOthers(a);
            const bShared = WorkspaceController.isFolderSharedWithOthers(b);
            if (aShared !== bShared) {
                if (mode === LibraryFolderSortMode.SHARED_FIRST) {
                    return aShared ? -1 : 1;
                }
                return aShared ? 1 : -1;
            }
        }
        return compareFoldersByName(a, b);
    });
}

export const SideBarItem = styled.div`
    width: 100%;
    color: #444;
    font-size: 14px;
    font-weight: 600;
    display: flex;
    flex-shrink: 0;
    align-items: flex-start;
    overflow: hidden;
    padding: 12px 10px;
    transition: background-color 300ms;
    cursor: pointer;
    gap: 10px;

    & > .material-icons {
        font-size: 18px;
        color: #666;
    }

    //.hover was added to fix a safari issue when hovering over a SideBarItem while dragging a presentation
    &:hover,
    &.hover {
        background: ${themeColors.hoverBlue};
    }

    &.selected {
        cursor: default;
        background: ${themeColors.lightBlue};

        & > .material-icons {
            color: ${themeColors.ui_blue};
        }

        //.hover was added to fix a safari issue when hovering over a SideBarItem while dragging a presentation
        &:hover,
        &.hover {
            background: ${themeColors.lightBlue};
        }
    }

    .folder-name {
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .dark-mode & {
        color: #ccc;
        background: none;

        & > .material-icons {
            color: #aaa;
        }

        //.hover was added to fix a safari issue when hovering over a SideBarItem while dragging a presentation
        &:hover {
            background: #555;
        }

        &.selected {
            background: #333;

            & > .material-icons {
                color: ${themeColors.ui_blue};
            }

            //.hover was added to fix a safari issue when hovering over a SideBarItem while dragging a presentation
            &:hover {
                background: #333;
            }
        }
    }
`;

const FolderPanelSideBarItem = styled(SideBarItem)`
    box-sizing: border-box;
    align-items: center;
    font-size: 12px;
    gap: 0;
    height: 30px;
    line-height: 16px;
    min-height: 30px;
    padding: 4px 8px;
    border-radius: 2px;

    & > .material-icons {
        color: #888;
        font-variation-settings: "FILL" 0;
        margin-right: 8px;
    }

    &:hover,
    &.hover {
        background: #eee;

        & > .material-icons {
            color: #11a9e2;
        }
    }

    &.selected {
        background: #e9e9e9;

        & > .material-icons {
            color: #11a9e2;
        }

         //.hover was added to fix a safari issue when hovering over a SideBarItem while dragging a presentation
        &:hover,
        &.hover {
            background: #e9e9e9;
        }
    }

    ${() => isCompactLibrary() && css`
        font-size: 14px;
        height: 38px;
        min-height: 38px;
        padding: 8px;

        &:hover,
        &.hover {
            background: transparent;

            & > .material-icons {
                color: #888;
            }
        }

        &.selected,
        &.selected:hover,
        &.selected.hover {
            background: #e9e9e9;

            & > .material-icons {
                color: #11a9e2;
            }
        }

        .dark-mode &.selected,
        .dark-mode &.selected:hover,
        .dark-mode &.selected.hover {
            background: #333;
        }
    `}
`;

const FolderContainer = styled.div`
    width: 100%;
    position: relative;
`;

const FolderLabel = styled.div`
    ${() => !isCompactLibrary() && css`
        padding: 0 8px;
    `}
`;

const SideBarLabel = styled.div`
    box-sizing: border-box;
    width: 100%;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.5px;
    display: flex;
    align-items: center;
    text-transform: uppercase;

    color: #888;
    height: 30px;
    min-height: 30px;
    padding: 0;

    .dark-mode & {
        color: #bbb;
    }

    ${() => isCompactLibrary() && css`
        height: 38px;
        min-height: 38px;
        padding: 0 8px;

        ${FolderLabel} {
            padding-left: 0;
        }
    `}
`;

const LibraryFolder = styled(FolderPanelSideBarItem)`
    gap: 0;

    .folder-name {
        flex: 1;
        line-height: 16px;
        min-width: 0;
        white-space: nowrap;
    }

    .MuiIconButton-root {
        padding: 0;
        opacity: 0;
        min-width: 0;
        width: 0;
        height: 18px;
        margin-left: auto;

        .MuiIcon-root,
        .bai-icon {
            color: #888 !important;
            font-size: 18px !important;
            width: 18px !important;
            height: 18px !important;
            line-height: 1 !important;
        }

        &:hover {
            .MuiIcon-root,
            .bai-icon {
                color: #555 !important;
            }
        }
    }

    .expand-arrow {
        font-size: 18px;
        margin-right: 0;
    }

    .folder-toggle-icon {
        position: relative;
        flex-shrink: 0;
        width: 18px;
        height: 18px;
        margin-right: 8px;

        & > .material-icons {
            position: absolute;
            inset: 0;
            font-size: 18px;
            color: #888;
            font-variation-settings: "FILL" 0;
        }

        .expand-arrow {
            opacity: 0;
        }
    }

    .mobile-folder-expand-arrow {
        display: none;
    }

    &:hover,
    &.hover {
        .folder-toggle-icon > .material-icons {
            color: #11a9e2;
        }

        .folder-toggle-icon.has-subfolders {
            .folder-icon {
                opacity: 0;
            }

            .expand-arrow {
                opacity: 1;
            }
        }

        .MuiIconButton-root {
            opacity: 1;
            width: 18px;
        }
    }

    &.selected .folder-toggle-icon > .material-icons {
        color: #11a9e2;
    }

    ${() => isCompactLibrary() && css`
        .MuiIconButton-root {
            display: none;
        }

        &:hover,
        &.hover {
            .folder-toggle-icon > .material-icons {
                color: #888;
            }
        }

        .folder-toggle-icon.has-subfolders .folder-icon {
            opacity: 1;
        }

        .mobile-folder-expand-arrow {
            display: flex;
            align-items: center;
            justify-content: end;
            width: 38px;
            height: 38px;
            color: #888;
            font-size: 18px;
            flex-shrink: 0;
            margin: 0px;
        }

        &.selected .folder-toggle-icon > .material-icons,
        &.selected .mobile-folder-expand-arrow {
            color: #11a9e2;
        }
    `}
`;

const SubLibraryFolder = styled(LibraryFolder)`
    padding-left: 28px;
    width: 100%;
`;

const SideBarWidthContainer = styled.div<{ $width: number }>`
    position: relative;
    flex-shrink: 0;
    align-self: flex-start;
    height: 100%;
    width: ${props => isCompactLibrary() ? "100%" : `${props.$width}px`};
    min-width: ${MIN_SIDEBAR_WIDTH}px;
    max-width: ${MAX_SIDEBAR_WIDTH}px;
    box-sizing: border-box;
`;

const SideBarPanel = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    background: #f9f9f9;
    overflow: hidden;
    box-sizing: border-box;
`;

const PaddedSection = styled.div`
    flex-shrink: 0;
    width: 100%;
    box-sizing: border-box;
    padding: 10px 10px 0 10px;
    display: flex;
    flex-direction: column;
`;

const PaddedFooter = styled.div`
    flex-shrink: 0;
    width: 100%;
    box-sizing: border-box;
    padding: 0 15px 15px;
`;

const StyledFolders = styled.div`
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    width: 100%;
    flex: 1;
    /* Required for overflow-y: auto inside a flex column child. */
    min-height: 0;

    padding: ${() => !isCompactLibrary() ? "0 8px" : "0 0 0 10px"};

`;

const TrashSideBarContainer = styled.div`
    width: 100%;
    /* Keep trash at full height when vertical space is tight. */
    flex-shrink: 0;
`;

const StyledAddIcon = styled(Icon).attrs({ className: "material-symbols-outlined" })`
    &&& {
        color: #888 !important;
        font-size: 16px !important;
        height: 16px !important;
        line-height: 1 !important;
        width: 16px !important;
    }
`;

const FolderSectionAddButton = styled(IconButton)`
    &&& {
        align-items: center;
        background-color: #e9e9e9;
        border-radius: 50%;
        box-sizing: border-box;
        display: flex;
        flex-shrink: 0;
        height: 20px;
        justify-content: center;
        line-height: 0;
        min-height: 20px;
        min-width: 20px;
        padding: 0;
        width: 20px;

        &:hover {
            background-color: #e9e9e9;

            ${StyledAddIcon} {
                color: #555 !important;
            }
        }
    }
`;

const FolderSectionActions = styled.div`
    display: flex;
    flex: 1;
    justify-content: flex-end;
    align-items: center;
    gap: 4px;
`;

const FolderSortIconMenu = styled(IconMenu)`
    &&& {
        padding: 0;
        transform: rotate(90deg);

        .bai-icon {
            color: #666;
            font-size: 18px;
        }

        &:hover {
            .MuiIcon-root,
            .material-icons {
                color: ${themeColors.ui_blue};
            }
        }
    }
`;

const FolderSortMenuHeader = styled(ListSubheader)`
    &&& {
        color: #888;
        font-size: 10px;
        font-weight: 600;
        letter-spacing: 0.5px;
        line-height: 20px;
        height: auto;
        padding: 8px 16px 4px;
        text-transform: uppercase;
    }
`;

// NOTE: We have to use `className="selected"` instead of `props.selected`
//   because a bug with `styled and` the application of `.dark-mode`
function selectedClass(selected: boolean) {
    return selected ? "selected" : "";
}

async function addDraggedPresentationsToFolder({
    folder,
    folderId,
    workspaceId,
    draggedItems,
}: {
    folder: Pick<IWorkspaceResourceFolder, "userGroupId">;
    folderId: string;
    workspaceId: string;
    draggedItems: PresentationLibraryPresentation[];
}) {
    const { write } = await getUserPermissionsToResourceFolder({
        workspaceId,
        folderId,
    });
    if (!write.object) {
        ShowWarningDialog({
            title: t("Permission denied"),
            message: t("You don't have permissions to add presentations to this folder. Contact your Admin to request access.")
        });
        ux.dragPresentationsToFolder.abandon({ folderId });
        return;
    }

    if (WorkspaceController.isFolderEffectivelySharedWithOthers(folder)) {
        const confirmed = await ShowConfirmationDialog({
            title: draggedItems.length === 1
                ? t("Add presentation to shared folder?")
                : t("Add {{count}} presentations to shared folder?", { count: draggedItems.length }),
            message: draggedItems.length === 1
                ? t("Everyone with access to this folder will be able to edit this presentation.")
                : t("Everyone with access to this folder will be able to edit these presentations."),
            okButtonLabel: t("Add"),
        });
        if (!confirmed) {
            ux.dragPresentationsToFolder.abandon({ folderId });
            return;
        }
    }

    await withProgressDialog({
        title: t("Moving presentations..."),
        action: () => WorkspaceController.updateWorkspaceResourceFolderResources({
            folderId,
            update: {
                add: draggedItems.map(item => ({
                    resourceId: item.id,
                    resourceType: WorkspaceResourceType.PRESENTATION,
                    permissions: { read: { object: true, permissions: true }, write: { object: true, permissions: true } }
                }))
            }
        })
    });
    ux.dragPresentationsToFolder.complete({ folderId });
}

interface PresentationLibrarySideBarProps {
    readOnly: boolean;
    filter: PresentationLibraryFilter;
    canEditLibraryItems: boolean;
    draggedItems: PresentationLibraryPresentation[];
    onSetFilter: (filter: PresentationLibraryFilter) => void;
    onHideSidebar?: () => void;
    onFolderDropStart?: () => void;
}

export const PresentationLibrarySideBar = WorkspaceController.withInitializedState(
    function PresentationLibrarySideBar(props: PresentationLibrarySideBarProps & WorkspaceControllerState) {
        const { readOnly, filter, onHideSidebar, onFolderDropStart, workspace: { id: workspaceId }, onSetFilter, resourceFolders, draggedItems } = props;

        const sideBarRef = useRef<HTMLDivElement>(null);
        const [sidebarWidth, setSidebarWidth] = useState(() => clampSidebarWidth(getWorkspaceResourcesSidebarWidth()));
        const [folderSortMode, setFolderSortMode] = useState(getLibraryFolderSortMode);

        const handleResize = useCallback((nextWidth: number) => {
            const clampedWidth = clampSidebarWidth(nextWidth);
            setSidebarWidth(clampedWidth);
            saveWorkspaceResourcesSidebarWidth(clampedWidth);
        }, []);

        const handleSetFolderSortMode = useCallback((mode: LibraryFolderSortMode) => {
            setFolderSortMode(mode);
            localStorage.setItem(LIBRARY_FOLDER_SORT_STORAGE_KEY, mode);
        }, []);

        const isGuest = WorkspaceController.isGuest;
        const canCreateFolders = WorkspaceController.actionPermissions[ActionPermissionsObject.RESOURCE_FOLDERS].create;
        const canUpgradeToCreateFolders = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.RESOURCE_FOLDERS, PermissionActionType.CREATE);
        const canUseSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].use;
        const canUpgradeToUseSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.USE);
        const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);

        const folders = sortLibraryFolders(
            resourceFolders.filter(folder =>
                !folder.parentFolderId &&
                (
                    folder.folderType === WorkspaceResourceFolderType.SHARED_DEFAULT ||
                    (
                        folder.folderType === WorkspaceResourceFolderType.SHARED &&
                        folder.resourcesType === WorkspaceResourceType.PRESENTATION
                    )
                )
            ),
            folderSortMode
        );
        // Hide Pro guests (guest without shared-resources use). Team guests keep use=true.
        const showFoldersSection =
            !(isGuest && !canUseSharedResources) &&
            (
                canCreateFolders ||
                folders.length > 0 ||
                canUseSharedResources ||
                (!isGuest && canUpgradeToCreateFolders) ||
                canUpgradeToUseSharedResources ||
                canUpgradeToManageSharedResources
            );
        const showAddFolder = !readOnly && !isCompactLibrary() && canCreateFolders;
        const showUpgradeFolders = !isGuest && canUpgradeToCreateFolders;

        const handleSelectFilter = (filterType: PresentationLibraryFilterType) => {
            onSetFilter({ type: filterType });
            onHideSidebar?.();
        };

        const handleTrashMouseUp = () => {
            if (draggedItems.length > 0) {
                PresentationLibraryPaneController.trashPresentations(draggedItems.map(p => p.id));
                return;
            }

            onSetFilter({ type: PresentationLibraryFilterType.TRASH });
        };

        const handleCreateFolder = async () => {
            if (!WorkspaceController.actionPermissions[ActionPermissionsObject.RESOURCE_FOLDERS].create) {
                if (WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.RESOURCE_FOLDERS, PermissionActionType.CREATE)) {
                    ShowUpgradeDialog({
                        type: UpgradePlanDialogType.UPGRADE_PLAN,
                        analytics: { cta: "Folder" },
                        workspaceId
                    });
                    return;
                }

                ShowWarningDialog({
                    title: t("Oops!"),
                    message: t("You do not have permission to create folders!")
                });
                return;
            }

            ShowDialog(CreateFolderDialog, { folderType: WorkspaceResourceFolderType.SHARED });
        };

        const shortcutsSection = (
            <>
                <FolderPanelSideBarItem
                    className={selectedClass(filter.type == PresentationLibraryFilterType.ALL_PRESENTATIONS)}
                    onClick={() => handleSelectFilter(PresentationLibraryFilterType.ALL_PRESENTATIONS)}
                    id="all-presentations"
                    data-test-id="sidebar-nav-all-presentations"
                >
                    <Icon className="material-symbols-outlined">select_all</Icon>
                    {t("All Presentations")}
                </FolderPanelSideBarItem>
                <FolderPanelSideBarItem
                    className={selectedClass(filter.type == PresentationLibraryFilterType.RECENT)}
                    onClick={() => handleSelectFilter(PresentationLibraryFilterType.RECENT)}
                    id="rect"
                    data-test-id="sidebar-nav-recent"
                >
                    <Icon className="material-symbols-outlined">schedule</Icon>
                    {t("Recent Presentations")}
                </FolderPanelSideBarItem>
                <FolderPanelSideBarItem
                    className={selectedClass(filter.type == PresentationLibraryFilterType.OWNED_BY_ME)}
                    onClick={() => handleSelectFilter(PresentationLibraryFilterType.OWNED_BY_ME)}
                    id="owned-by-me"
                    data-test-id="sidebar-nav-owned-by-me"
                >
                    <Icon className="material-symbols-outlined">person</Icon>
                    {t("Created By Me")}
                </FolderPanelSideBarItem>
                <FolderPanelSideBarItem
                    className={selectedClass(filter.type == PresentationLibraryFilterType.SHARED_WITH_ME)}
                    onClick={() => handleSelectFilter(PresentationLibraryFilterType.SHARED_WITH_ME)}
                    id="shared-with-me"
                    data-test-id="sidebar-nav-shared-with-me"
                >
                    <Icon className="material-symbols-outlined">group</Icon>
                    {t("Shared With Me")}
                </FolderPanelSideBarItem>
            </>
        );

        const foldersSection = (
            <StyledFolders>
                {showFoldersSection && <>
                    <SideBarLabel data-test-id="folders-section">
                        <FolderLabel>{t("Folders")}</FolderLabel>
                        {!isCompactLibrary() && showAddFolder &&
                            <FolderSectionAddButton
                                size="small"
                                id="create-folder-button"
                                data-test-id="create-folder-button"
                                onClick={handleCreateFolder}
                            >
                                <StyledAddIcon>add</StyledAddIcon>
                            </FolderSectionAddButton>
                        }
                        <FolderSectionActions>
                            {!isCompactLibrary() && showUpgradeFolders &&
                                <UpgradeTooltip
                                    tooltipTitle="Unlock Presentation Folders"
                                    tooltipText="Upgrade your plan to organize your presentations into folders."
                                    onUpgrade={() => ShowDialog(ChangePlanDialog, {
                                        featuredPlan: WorkspacePlanTier.PRO,
                                        analytics: { cta: "unlock_folders" }
                                    })}
                                >
                                    <UpgradeIcon />
                                </UpgradeTooltip>
                            }
                            {canUseSharedResources &&
                                <FolderSortIconMenu
                                    icon="compare_arrows"
                                    size="small"
                                    fontSize="small"
                                    ariaLabel={t("Sort folders")}
                                    data-test-id="folder-sort-menu"
                                >
                                    <FolderSortMenuHeader disableSticky>{t("Sort By")}</FolderSortMenuHeader>
                                    <MenuItem
                                        selected={folderSortMode === LibraryFolderSortMode.NAME}
                                        onClick={() => handleSetFolderSortMode(LibraryFolderSortMode.NAME)}
                                        data-test-id="folder-sort-name"
                                    >
                                        <Icon style={{ fontSize: "20px" }} className="material-symbols-outlined">sort_by_alpha</Icon>
                                        {t("Name")}
                                    </MenuItem>
                                    <MenuItem
                                        selected={folderSortMode === LibraryFolderSortMode.SHARED_FIRST}
                                        onClick={() => handleSetFolderSortMode(LibraryFolderSortMode.SHARED_FIRST)}
                                        data-test-id="folder-sort-shared-first"
                                    >
                                        <Icon style={{ fontSize: "20px" }} className="material-symbols-outlined">folder_shared</Icon>
                                        {t("Shared folders first")}
                                    </MenuItem>
                                    <MenuItem
                                        selected={folderSortMode === LibraryFolderSortMode.PRIVATE_FIRST}
                                        onClick={() => handleSetFolderSortMode(LibraryFolderSortMode.PRIVATE_FIRST)}
                                        data-test-id="folder-sort-private-first"
                                    >
                                        <Icon style={{ fontSize: "20px" }} className="material-symbols-outlined">folder</Icon>
                                        {t("Private folders first")}
                                    </MenuItem>
                                </FolderSortIconMenu>
                            }
                        </FolderSectionActions>
                    </SideBarLabel>
                    <FolderContainer>
                        {folders.map((folder, idx) => (
                            <SharedFolder
                                key={idx}
                                folder={folder}
                                readOnly={readOnly}
                                onSetFilter={onSetFilter}
                                draggedItems={draggedItems}
                                filter={filter}
                                onHideSidebar={onHideSidebar}
                                onFolderDropStart={onFolderDropStart}
                            />
                        ))}
                    </FolderContainer>
                </>}
            </StyledFolders>
        );

        const trashSection = !readOnly && !isCompactLibrary() ? (
            <TrashSideBarContainer>
                <Divider margin={0} />
                <FolderPanelSideBarItem
                    className={selectedClass(filter.type == PresentationLibraryFilterType.TRASH)}
                    onMouseUp={handleTrashMouseUp}
                    id="trash"
                    data-test-id="sidebar-nav-trash"
                >
                    <Icon className="material-symbols-outlined">delete</Icon>
                    {t("Trash")}
                </FolderPanelSideBarItem>
            </TrashSideBarContainer>
        ) : null;

        if (isCompactLibrary()) {
            return (
                <SideBarWidthContainer ref={sideBarRef} $width={260}>
                    <SideBarPanel>
                        <PaddedSection>
                            {shortcutsSection}
                            <Divider margin={10} />
                        </PaddedSection>
                        {foldersSection}
                        {trashSection}
                    </SideBarPanel>
                </SideBarWidthContainer>
            );
        }

        return (
            <SideBarWidthContainer ref={sideBarRef} $width={sidebarWidth}>
                <SideBarPanel>
                    <PaddedSection>
                        {shortcutsSection}
                        {/* @ts-ignore */}
                        <Divider margin={10} />
                    </PaddedSection>
                    {foldersSection}
                    {trashSection && <PaddedFooter>{trashSection}</PaddedFooter>}
                </SideBarPanel>
                {!isCompactLibrary() && <UIPaneSideBarResizer
                    // @ts-expect-error UiComponents is untyped JS
                    sideBarRef={sideBarRef}
                    onResize={handleResize}
                />}
            </SideBarWidthContainer>
        );
    }
);

interface FolderProps {
    folder: IWorkspaceResourceFolder;
    readOnly: boolean;
    onSetFilter: (filter: PresentationLibraryFilter) => void;
    draggedItems: PresentationLibraryPresentation[];
    filter: PresentationLibraryFilter;
    onHideSidebar?: () => void;
    onFolderDropStart?: () => void;
}

const SharedFolder = WorkspaceController.withInitializedState(function SharedFolder(props: FolderProps & WorkspaceControllerState) {
    const { folder, workspace: { id: workspaceId }, onHideSidebar, onFolderDropStart, filter, readOnly, onSetFilter, draggedItems, resourceFolders } = props;
    const [permissions, setPermissions] = useState<IWorkspacePermissionAccess>(GET_DEFAULT_RESTRICTED_PERMISSIONS_ACCESS());
    const [isExpanded, setIsExpanded] = useState(false);
    const ignoreNextFolderMouseUpRef = useRef(false);

    const subFolders = resourceFolders
        .filter(f => f.resourcesType === WorkspaceResourceType.PRESENTATION && f.folderType === WorkspaceResourceFolderType.SHARED && f.parentFolderId === folder.id)
        .sort((a, b) => a.name.localeCompare(b.name));
    const expandIcon = isExpanded ? "arrow_drop_down" : "arrow_right";
    const isSharedWithOthers = WorkspaceController.isFolderSharedWithOthers(folder);
    const folderIcon = isSharedWithOthers ? "folder_shared" : "folder";

    useEffect(() => {
        if (
            filter.type === PresentationLibraryFilterType.FOLDER &&
            filter.folderId === folder.id
        ) {
            setIsExpanded(true);
        }
    }, [filter.type, filter.folderId, filter.subFolderId, folder.id]);

    const handleToggleExpanded = (event: React.MouseEvent | React.TouchEvent) => {
        event.stopPropagation();
        event.preventDefault();
        setIsExpanded(expanded => !expanded);
    };

    useEffect(() => {
        (async () => {
            try {
                const fetchedPermissions = await getUserPermissionsToResourceFolder({
                    workspaceId: workspaceId,
                    folderId: folder.id,
                });
                setPermissions(fetchedPermissions);
            } catch (err) {
                logger.error(err, "[PresentationLibrarySideBar][SharedFolder] useEffect() Unable to fetch permissions");
            }
        })();
    }, [folder.id]);

    const handleMouseUp = async () => {
        if (ignoreNextFolderMouseUpRef.current) {
            return;
        }

        if (draggedItems.length === 0) {
            onSetFilter({ type: PresentationLibraryFilterType.FOLDER, folderId: folder.id });
            if (!isCompactLibrary() && subFolders.length > 0) {
                setIsExpanded(true);
            }
            if (isCompactLibrary() || subFolders.length === 0) {
                onHideSidebar?.();
            }
            return;
        }

        onFolderDropStart?.();

        try {
            await addDraggedPresentationsToFolder({
                folder,
                folderId: folder.id,
                workspaceId,
                draggedItems,
            });
        } catch (err) {
            logger.error(err, "[PresentationLibrarySideBar][SharedFolder] handleMouseUp() Unable to move presentations into folder");
            ux.dragPresentationsToFolder.fail({ error: err, folderId: folder.id });

            ShowErrorDialog({
                title: t("Unable to move presentations into folder"),
                message: err.message
            });
        }
    };

    return (
        <Fragment>
            <LibraryFolder
                data-attr={folder.name}
                data-attr-id={folder.id}
                data-folder-drop-target="true"
                className={selectedClass(filter.type == PresentationLibraryFilterType.FOLDER && filter.folderId == folder.id && !filter.subFolderId)}
                onMouseUp={handleMouseUp}
                onTouchEnd={handleMouseUp}
            >
                <div
                    className={`folder-toggle-icon ${subFolders.length > 0 ? "has-subfolders" : ""}`}
                    onMouseUp={!isCompactLibrary() && subFolders.length > 0 ? handleToggleExpanded : undefined}
                >
                    <Icon className="material-symbols-outlined folder-icon">{folderIcon}</Icon>
                    {!isCompactLibrary() && subFolders.length > 0 &&
                        <Icon className="material-symbols-outlined expand-arrow">{expandIcon}</Icon>
                    }
                </div>
                <div className="folder-name">
                    {folder.name}
                </div>
                {isCompactLibrary() && subFolders.length > 0 &&
                    <Icon
                        className="material-symbols-outlined mobile-folder-expand-arrow"
                        onMouseUp={handleToggleExpanded}
                        onTouchEnd={handleToggleExpanded}
                    >
                        {expandIcon}
                    </Icon>
                }
                {!readOnly && !isCompactLibrary() &&
                    <IconMenu
                        disableAutoFocusItem
                        onShowMenu={() => {
                            ignoreNextFolderMouseUpRef.current = true;
                            onSetFilter({ type: PresentationLibraryFilterType.FOLDER, folderId: folder.id });
                        }}
                        handleCloseMenu={() => {
                            ignoreNextFolderMouseUpRef.current = false;
                        }}
                    >
                        <FolderMenuItems
                            folder={folder}
                            resourceFolders={resourceFolders}
                            onSetFilter={onSetFilter}
                            permissions={permissions}
                        />
                    </IconMenu>
                }
            </LibraryFolder>
            {isExpanded &&
                subFolders.map(subFolder => (
                    <SharedSubFolder
                        key={subFolder.id}
                        folder={subFolder}
                        readOnly={readOnly}
                        filter={filter}
                        onSetFilter={onSetFilter}
                        draggedItems={draggedItems}
                        permissions={permissions}
                        onHideSidebar={onHideSidebar}
                        onFolderDropStart={onFolderDropStart}
                    />
                ))}
        </Fragment >
    );
});

const SharedSubFolder = WorkspaceController.withInitializedState(function SharedSubFolder(props: FolderProps & WorkspaceControllerState & { permissions: IWorkspacePermissionAccess }) {
    const { folder, readOnly, filter, onHideSidebar, onFolderDropStart, onSetFilter, permissions, resourceFolders, draggedItems, workspace: { id: workspaceId } } = props;
    const ignoreNextFolderMouseUpRef = useRef(false);

    const handleMouseUp = async () => {
        if (ignoreNextFolderMouseUpRef.current) {
            return;
        }

        if (draggedItems.length === 0) {
            onSetFilter({ type: PresentationLibraryFilterType.FOLDER, folderId: folder.parentFolderId, subFolderId: folder.id });
            onHideSidebar?.();
            return;
        }

        onFolderDropStart?.();

        try {
            await addDraggedPresentationsToFolder({
                folder,
                folderId: folder.id,
                workspaceId,
                draggedItems,
            });
        } catch (err) {
            logger.error(err, "[PresentationLibrarySideBar][SharedSubFolder] handleMouseUp() Unable to move presentations into folder");
            ux.dragPresentationsToFolder.fail({ error: err, folderId: folder.id });

            ShowErrorDialog({
                title: t("Unable to move presentations into folder"),
                message: err.message
            });
        }
    };

    return (
        <SubLibraryFolder
            data-attr={folder.name}
            data-folder-drop-target="true"
            className={selectedClass(filter.type == PresentationLibraryFilterType.FOLDER && filter.subFolderId == folder.id)}
            onMouseUp={handleMouseUp}
            onTouchEnd={handleMouseUp}
        >
            <Icon className="material-symbols-outlined">subdirectory_arrow_right</Icon>
            <div className="folder-name">{folder.name}</div>

            {!readOnly && !isCompactLibrary() && !WorkspaceController.isGuest && permissions.write.object &&
                <IconMenu
                    onShowMenu={() => {
                        ignoreNextFolderMouseUpRef.current = true;
                        onSetFilter({ type: PresentationLibraryFilterType.FOLDER, folderId: folder.parentFolderId, subFolderId: folder.id });
                    }}
                    handleCloseMenu={() => {
                        ignoreNextFolderMouseUpRef.current = false;
                    }}
                >
                    <SubFolderMenuItems
                        folder={folder}
                        resourceFolders={resourceFolders}
                        onSetFilter={onSetFilter}
                        permissions={permissions}
                    />
                </IconMenu>
            }
        </SubLibraryFolder>
    );
});
